import React from 'react';
import Icon from '../../../components/AppIcon';

const ResponseOverview = ({ auditData }) => {
  const overviewMetrics = [
    {
      label: 'Completion Rate',
      value: `${auditData?.responseRate}%`,
      icon: 'TrendingUp',
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      trend: '+5% from last week'
    },
    {
      label: 'Average Score',
      value: `${auditData?.averageScore}%`,
      icon: 'Award',
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      trend: '+2% from target'
    },
    {
      label: 'Pending Responses',
      value: auditData?.totalUsers - auditData?.completedUsers,
      icon: 'Clock',
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
      trend: '10 users remaining'
    },
    {
      label: 'Photo Submissions',
      value: 127,
      icon: 'Camera',
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      trend: '89% compliance'
    }
  ];

  const categoryScores = [
    { category: 'General Safety', score: 92, responses: 35, color: 'bg-green-500' },
    { category: 'Fire Safety', score: 85, responses: 35, color: 'bg-blue-500' },
    { category: 'Emergency Procedures', score: 81, responses: 35, color: 'bg-yellow-500' },
    { category: 'PPE Compliance', score: 88, responses: 35, color: 'bg-purple-500' },
    { category: 'Equipment Safety', score: 79, responses: 35, color: 'bg-orange-500' }
  ];

  return (
    <div className="space-y-6">
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {overviewMetrics?.map((metric, index) => (
          <div
            key={index}
            className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-lg ${metric?.bgColor} flex items-center justify-center`}>
                <Icon name={metric?.icon} size={24} className={metric?.color} />
              </div>
            </div>
            <div className="text-3xl font-bold text-foreground mb-1">
              {metric?.value}
            </div>
            <div className="text-sm text-muted-foreground mb-2">
              {metric?.label}
            </div>
            <div className="text-xs text-muted-foreground">
              {metric?.trend}
            </div>
          </div>
        ))}
      </div>
      {/* Category Scores */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">Category-wise Performance</h3>
        <div className="space-y-4">
          {categoryScores?.map((item, index) => (
            <div key={index}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-foreground">{item?.category}</span>
                  <span className="text-xs text-muted-foreground">
                    ({item?.responses} responses)
                  </span>
                </div>
                <span className="text-sm font-bold text-foreground">{item?.score}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-3">
                <div
                  className={`${item?.color} h-3 rounded-full transition-all`}
                  style={{ width: `${item?.score}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Response Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Response Status</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="CheckCircle" size={20} className="text-green-500" />
                <span className="text-sm font-medium text-foreground">Completed</span>
              </div>
              <span className="text-lg font-bold text-green-600">{auditData?.completedUsers}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-orange-500/10 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={20} className="text-orange-500" />
                <span className="text-sm font-medium text-foreground">In Progress</span>
              </div>
              <span className="text-lg font-bold text-orange-600">7</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-500/10 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="Circle" size={20} className="text-gray-500" />
                <span className="text-sm font-medium text-foreground">Not Started</span>
              </div>
              <span className="text-lg font-bold text-gray-600">
                {auditData?.totalUsers - auditData?.completedUsers - 7}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Compliance Indicators</h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Photo Compliance</span>
                <span className="text-sm font-bold text-foreground">89%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-green-500 h-2 rounded-full" style={{ width: '89%' }} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Answer Completeness</span>
                <span className="text-sm font-bold text-foreground">94%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-blue-500 h-2 rounded-full" style={{ width: '94%' }} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">On-time Submission</span>
                <span className="text-sm font-bold text-foreground">76%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '76%' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponseOverview;